import { useMemo } from "react";
import { useQuery } from "@powersync/react";
import type { Hierarchy } from "@/lib/levels";

// The left navigator: Sections, each with Modules (a filtered or task-specific
// view of that section). Every module is a real URL (path + search), so it can
// be bookmarked, shared and opened in a new tab.

export interface NavModule {
  id: string;
  label: string;
  path: string; // under /t/:team, e.g. "tasks" or "dashboards/team"
  search: Record<string, string>;
  count?: number;
  tone?: "alert" | "info";
  action?: boolean; // e.g. "Create a task": an action, never shown as the active view
}
export interface NavSection {
  id: string;
  icon: string;
  label: string;
  path: string;
  group?: string;
  match: string[]; // first path segments that belong to this section
  modules: NavModule[];
}

interface Counts { review: number; live: number; myTasks: number; activeTasks: number; overdueTasks: number; needsSig: number; failedReports: number; stale: number }

export function useNavCounts(teamId: string, userId: string, bottomLevel: string): Counts {
  const today = new Date().toLocaleDateString("en-CA");
  const week = new Date(Date.now() + 7 * 86_400_000).toLocaleDateString("en-CA");
  const { data } = useQuery<Counts>(
    `SELECT
      (SELECT count(*) FROM mobile_field_notes fn JOIN map_features mf ON mf.id = fn.map_feature_id JOIN maps m ON m.id = mf.map_id
         WHERE m.team_id = ?1 AND fn.completed_at IS NOT NULL AND coalesce(fn.processing_state, 'completed') IN ('completed', 'failed')
         AND EXISTS (SELECT 1 FROM mobile_custom_report_statuses r WHERE r.field_note_id = fn.id AND r.approved_at IS NULL)) AS review,
      (SELECT count(*) FROM mobile_field_notes fn JOIN map_features mf ON mf.id = fn.map_feature_id JOIN maps m ON m.id = mf.map_id
         WHERE m.team_id = ?1 AND (fn.completed_at IS NULL OR coalesce(fn.processing_state, 'completed') NOT IN ('completed', 'failed'))) AS live,
      (SELECT count(*) FROM mobile_reminders WHERE completed_at IS NULL AND canceled_at IS NULL AND (assigned_user_id IS NULL OR assigned_user_id = ?2)) AS myTasks,
      (SELECT count(*) FROM mobile_reminders WHERE completed_at IS NULL AND canceled_at IS NULL AND coalesce(reminder_date, ?3) <= ?4) AS activeTasks,
      (SELECT count(*) FROM mobile_reminders WHERE completed_at IS NULL AND canceled_at IS NULL AND reminder_date < ?3) AS overdueTasks,
      (SELECT count(*) FROM mobile_custom_report_statuses r JOIN map_features mf ON mf.id = r.map_feature_id JOIN maps m ON m.id = mf.map_id
         WHERE m.team_id = ?1 AND r.approved_at IS NULL AND coalesce(r.status, 'completed') <> 'failed') AS needsSig,
      (SELECT count(*) FROM mobile_custom_report_statuses r JOIN map_features mf ON mf.id = r.map_feature_id JOIN maps m ON m.id = mf.map_id
         WHERE m.team_id = ?1 AND r.status = 'failed') AS failedReports,
      (SELECT count(*) FROM map_features mf JOIN maps m ON m.id = mf.map_id WHERE m.team_id = ?1 AND mf.level = ?5
         AND coalesce((SELECT max(coalesce(fn.completed_at, fn.started_at)) FROM mobile_field_notes fn WHERE fn.map_feature_id = mf.id), '1970') < datetime('now', '-21 days')) AS stale`,
    [teamId, userId, today, week, bottomLevel],
  );
  return data[0] ?? { review: 0, live: 0, myTasks: 0, activeTasks: 0, overdueTasks: 0, needsSig: 0, failedReports: 0, stale: 0 };
}

const mod = (path: string, id: string, label: string, search: Record<string, string> = {}, extra: Partial<NavModule> = {}): NavModule => ({ id, label, path, search, ...extra });

export function useNavModel(h: Hierarchy, userId: string, c: Counts): NavSection[] {
  return useMemo(() => {
    const top = h.levels[0];
    const bottom = h.levels.at(-1)!;
    const sections: NavSection[] = [
      { id: "home", icon: "home", label: "Home", path: "", match: [""], modules: [] },
      {
        id: "visits", icon: "tractor", label: "Field Visits", path: "visits", group: "Field work", match: ["visits"],
        modules: [
          mod("visits", "all", "All visits"),
          mod("visits", "review", "Needs review", { status: "review" }, { count: c.review, tone: "alert" }),
          mod("visits", "active", "In the field", { status: "active" }, { count: c.live, tone: "info" }),
          mod("visits", "signed", "Signed", { status: "signed" }),
          mod("visits", "mine", "My visits", { who: userId }),
        ],
      },
      {
        id: "tasks", icon: "check", label: "Tasks", path: "tasks", group: "Field work", match: ["tasks"],
        modules: [
          mod("tasks", "new", "Create a task", { new: "1" }, { action: true }),
          mod("tasks", "mine", "My tasks", { scope: "mine" }, { count: c.myTasks }),
          mod("tasks", "active", "Active tasks", { scope: "active" }, { count: c.activeTasks, tone: c.overdueTasks ? "alert" : undefined }),
          mod("tasks", "all", "All tasks"),
          mod("tasks", "done", "Completed", { scope: "done" }),
        ],
      },
      {
        id: "map", icon: "map", label: "Map", path: "map", group: "Field work", match: ["map"],
        modules: h.levels.slice().reverse().map((l, i) => mod("map", `map-${l.key}`, `${l.plural} on the map`, i === 0 ? {} : { level: l.key })),
      },
      {
        id: "dashboards", icon: "bar-chart", label: "Dashboards", path: "dashboards/team", group: "Dashboards", match: ["dashboards"],
        modules: [
          mod("dashboards/team", "team", "Team activity"),
          mod("dashboards/pipeline", "pipeline", "Sales pipeline"),
          mod("dashboards/metrics", "metrics", "Metrics"),
        ],
      },
      {
        id: "locations", icon: top.key === "farm" ? "barn" : "map-pin", label: top.plural, path: "locations", group: "Maps & data", match: ["locations"],
        modules: [
          ...h.levels.map((l, i) => mod("locations", `level-${l.key}`, `All ${l.plural.toLowerCase()}`, i === 0 ? {} : { level: l.key })),
          mod("locations", "stale", "Not visited in 3 weeks", { level: bottom.key, stale: "1" }, { count: c.stale, tone: "alert" }),
        ],
      },
      {
        id: "clients", icon: "user", label: "Clients", path: "clients", group: "Maps & data", match: ["clients", "contacts"],
        modules: [mod("clients", "all", "Client portfolio"), mod("clients/paper-trail", "paper", "Paper Trail")],
      },
      { id: "ask", icon: "search", label: "Ask Propel", path: "ask", group: "Maps & data", match: ["ask"], modules: [] },
      {
        id: "reports", icon: "clipboard", label: "Reports", path: "reports", group: "Business", match: ["reports"],
        modules: [
          mod("reports", "needs", "Needs signature", { status: "needs" }, { count: c.needsSig, tone: "alert" }),
          mod("reports", "signed", "Signed", { status: "signed" }),
          mod("reports", "all", "All reports"),
          ...(c.failedReports ? [mod("reports", "failed", "Failed", { status: "failed" }, { count: c.failedReports, tone: "alert" })] : []),
        ],
      },
      {
        id: "settings", icon: "settings", label: "Settings", path: "settings", group: "Business", match: ["settings"],
        modules: [
          mod("settings", "profile", "Profile"),
          mod("settings", "appearance", "Appearance", { section: "appearance" }),
          mod("settings", "team", "Team", { section: "team" }),
          mod("settings", "members", "Members", { section: "members" }),
          mod("settings", "hierarchy", "Level hierarchy", { section: "hierarchy" }),
          mod("settings", "sync", "Data & sync", { section: "sync" }),
        ],
      },
    ];
    return sections;
  }, [h, userId, c]);
}

export function sectionFor(sections: NavSection[], rest: string): NavSection | undefined {
  const seg = rest.split("/")[0] || "";
  return sections.find((s) => s.match.includes(seg));
}

// The active module is the most specific one whose search params all match the
// current URL; the section's param-less module is the fallback.
// Active module = same path, and the most specific search match; a module
// whose path is deeper (dashboards/team) matches by path prefix.
const FILTER_KEYS = ["status", "scope", "level", "stale", "section", "who"];
export function activeModule(section: NavSection | undefined, rest: string, search: Record<string, unknown>): string | null {
  if (!section?.modules.length) return null;
  let best: NavModule | null = null;
  for (const m of section.modules) {
    if (m.action) continue;
    if (!(rest === m.path || rest.startsWith(m.path + "/"))) continue;
    const keys = Object.keys(m.search);
    const ok = keys.every((k) => String(search[k] ?? "") === m.search[k]);
    const extra = Object.keys(search).some((k) => search[k] !== undefined && !(k in m.search) && FILTER_KEYS.includes(k));
    if (ok && !extra && (!best || m.path.length > best.path.length || keys.length > Object.keys(best.search).length)) best = m;
  }
  return best?.id ?? null;
}
